import { Button, Input, Typography } from "@mui/joy";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form_Entity } from "../../entities/form";
import {
  formOpenSelector,
  selectedFormIDSelector,
  setFormOpen,
  setSelectedFormID,
} from "../../reducers/rhapsody";
import {
  useCreateFormMutation,
  useUpdateFormMutation,
} from "../../redux/form/formEndpoints";
import { useForm } from "../../redux/form/formHooks";
import DialogClose from "../dialogClose/dialogClose";
import Upload from "../fileUpload/fileUpload";

const parseConfig = (config?: string | null): Record<string, any> => {
  if (!config) return {};
  try {
    return JSON.parse(config);
  } catch (e) {
    console.warn("Invalid form config JSON; falling back to empty object", e);
    return {};
  }
};

/**
 *
 * @returns {ReactElement} FormForm page
 */
export function FormForm() {
  const open = useSelector(formOpenSelector("form"));
  const dispatch = useDispatch();
  const selectedFormID = useSelector(selectedFormIDSelector);
  const { form } = useForm(selectedFormID);
  const [createForm, { isLoading: l1 }] = useCreateFormMutation();
  const [updateForm, { isLoading: l2 }] = useUpdateFormMutation();
  const [_form, setForm] = useState<Partial<Form_Entity>>({
    name: "",
  });
  const [organizationIDs, setOrganizationIDs] = useState("");

  useEffect(() => {
    if (form?.id) {
      setForm(form);
      const cfg = parseConfig(form.config);
      setOrganizationIDs(cfg.organizationIDs ?? "");
    } else {
      setOrganizationIDs("");
    }
  }, [form]);

  const close = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "form" }));
    dispatch(setSelectedFormID(null));
  };

  const save = () => {
    const cfg = parseConfig(_form.config);
    const nextConfig = { ...cfg, organizationIDs };
    const nextForm: Partial<Form_Entity> = {
      ..._form,
      config: JSON.stringify(nextConfig),
    };

    if (_form.id) {
      updateForm({ id: _form.id, body: nextForm });
    } else {
      createForm(nextForm);
    }
    close();
  };

  return (
    <DialogClose open={open} onClose={close}>
      <DialogTitle>
        <Typography
          level="title-lg"
          startDecorator={<i className="fa-regular fa-file-lines"></i>}
        >
          {_form?.name ? _form?.name : "New Form"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
        <Input
          value={_form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          autoFocus
          startDecorator="Name:"
          sx={{ boxShadow: "none" }}
        />
        <Input
          value={_form.code}
          onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
          autoFocus
          startDecorator="Code:"
          sx={{ boxShadow: "none" }}
        />
        <Input
          value={_form.type}
          onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
          autoFocus
          startDecorator="Type:"
          sx={{ boxShadow: "none" }}
        />
        <Input
          value={_form.url}
          onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
          autoFocus
          startDecorator="URL:"
          sx={{ boxShadow: "none" }}
        />
        <Upload
          onUpload={(e) => {
            setForm((s) => ({
              ...s,
              url: e,
            }));
          }}
        />
        <Input
          value={organizationIDs}
          onChange={(e) => setOrganizationIDs(e.target.value)}
          startDecorator="Organization IDs:"
          sx={{ boxShadow: "none" }}
          placeholder="Comma-separated IDs"
        />
      </DialogContent>
      <DialogActions>
        <Button variant="soft" color="neutral" onClick={close}>
          Close
        </Button>
        <Button loading={l1 || l2} onClick={save} disabled={!_form.name}>
          {selectedFormID ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
